import {
  ConfigurationContext,
  FormTab,
  HasuraDataTableColumnDef,
  DataTableExRef,
} from '@kirz/mui-admin';
import { Add, AddLink } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useContext, useRef, useState } from 'react';

import { EstatesTable } from 'components/EstatesTable';
import { LinkEstateToClientDialog } from 'components/LinkEstateToClientDialog';
import { TagsGroup } from 'components/TagsGroup';
import {
  Finishing,
  getSaleQueueName,
  HouseTypes,
  PaymentForms,
  RoomsNumbers,
  Types,
} from 'constants/estate';
import { FunnelStageType } from 'constants/funnels';
import { SettingsContext } from 'contexts/SettingsContext';

export default function EstatesTab(props: { clientId: number }) {
  const { clientId } = props;
  const { hasura } = useContext(ConfigurationContext);
  const [selectedTab, setSelectedTab] = useState<string>('all');
  const [addMode, setAddMode] = useState<'new' | 'link'>('new');
  const tableRef = useRef<DataTableExRef>(null);
  const { estateStatuses } = useContext(SettingsContext);

  const filterColumns = useCallback(
    (columns: HasuraDataTableColumnDef[]) => {
      const hiddenColumns = (
        [
          {
            headerName: 'Клиент',
            field: 'client',
            width: 140,
            type: 'relationship',
            selector:
              'client { data { *shortFullName* } user { shortFullName isRemoved } }',
          },
          {
            headerName: 'Реферер',
            field: 'referrer',
            width: 110,
            selector: false,
            type: 'relationship',
            isRemovedGetter: (row) => row.client?.user?.isRemoved,
            valueGetter: ({ row }) => row.client?.user?.shortFullName,
            onSort: (sort) => [{ client: { user: { fullName: sort } } }],
          },
          {
            headerName: 'Эксперт',
            field: 'user',
            width: 110,
            selector: 'user { data { *shortFullName* } }',
            type: 'relationship',
          },

          {
            headerName: 'Актуальная стоимость',
            field: 'actualPrice',
            width: 120,
            selector: 'price { actualPrice }',
            hidden: true,
            valueGetter({ row }) {
              return (
                row.price?.actualPrice &&
                `${row.price?.actualPrice.toLocaleString()}₽`
              );
            },
            onSort(sort) {
              return [{ price: { actualPrice: sort } }];
            },
          },

          {
            headerName: 'Застройщик',
            field: 'developer',
            width: 110,
            hidden: true,
          },
          {
            headerName: 'Тип дома',
            field: 'houseType',
            width: 110,
            type: 'select',
            items: HouseTypes,
            hidden: true,
          },
          {
            headerName: 'Форма оплаты',
            field: 'dealsInfo { purchaseDeal { *paymentForm* } }',
            width: 110,
            hidden: true,
            valueGetter({ row }) {
              return PaymentForms.find(
                (x) => x.value === row.dealsInfo?.purchaseDeal?.paymentForm,
              )?.text;
            },
          },
          {
            headerName: 'Стоимость покупки',
            field: 'price { *purchasePrice* }',
            width: 110,
            type: 'relationship',
            fetchRemoved: false,
            hidden: true,
            valueGetter({ row }) {
              return row.price?.purchasePrice?.toLocaleString();
            },
          },
          {
            headerName: 'Дата покупки',
            field: 'price { *purchaseDate* }',
            hidden: true,
            valueGetter({ row }) {
              return row.price?.purchaseDate;
            },
            width: 110,
            type: 'date',
            fetchRemoved: false,
          },
          {
            headerName: 'Название банка, в котором взята ипотека',
            field: 'dealsInfo { purchaseDeal { *mortgageBankName* } }',
            width: 110,
            type: 'relationship',
            hidden: true,
          },
          {
            headerName: 'Собственник',
            field: 'owner',
            selector:
              'owner: clients(where:{isOwner: {_eq:true} }, limit: 1) { client { shortFullName } }',
            valueGetter({ value }) {
              return value?.length ? value[0].client.shortFullName : null;
            },
            width: 110,
            sortable: false,
            hidden: true,
          },
        ] as HasuraDataTableColumnDef[]
      ).map((x) => ({ ...x, hidden: true }));

      const soldColumns = [
        { headerName: 'ID', field: 'id', width: 60 },
        {
          headerName: 'ЖК',
          field: 'residentialComplex',
          type: 'relationship',
          flex: 1,
          selector: 'residentialComplex { *name* }',
        },
        {
          headerName: 'Адрес',
          field: 'realAddress',
          flex: 1,
        },
        {
          headerName: 'Стоимость покупки',
          field: 'purchasePrice',
          selector: 'dealsInfo { saleDeal { dealPrice } }',
          type: 'relationship',
          fetchRemoved: false,
          valueGetter({ row }) {
            return row.dealsInfo?.saleDeal?.dealPrice?.toLocaleString();
          },
        },
        {
          headerName: 'Дата покупки',
          field: 'purchaseDate',
          selector: 'dealsInfo { saleDeal { dealDate } }',
          valueGetter: ({ row }) =>
            row.dealsInfo?.saleDeal?.dealDate &&
            dayjs(row.dealsInfo?.saleDeal?.dealDate).format('DD.MM.YYYY'),
        },
        {
          headerName: 'Стоимость продажи',
          field: 'salePrice',
          selector: 'dealsInfo { purchaseDeal { dealPrice } }',
          type: 'relationship',
          fetchRemoved: false,
          valueGetter({ row }) {
            return row.dealsInfo?.purchaseDeal?.dealPrice?.toLocaleString();
          },
        },
        {
          headerName: 'Дата продажи',
          field: 'saleDate',
          selector: 'dealsInfo { purchaseDeal { dealDate } }',
          valueGetter: ({ row }) =>
            row.dealsInfo?.purchaseDeal?.dealDate &&
            dayjs(row.dealsInfo?.purchaseDeal?.dealDate).format('DD.MM.YYYY'),
        },
        {
          headerName: 'Доходность (%)',
          field: 'yearlyIncome',
          selector: 'dealsInfo { purchaseDeal { price { yearlyIncome } } }',
          onSort: (sort) => [
            { dealsInfo: { purchaseDeal: { price: { yearlyIncome: sort } } } },
          ],
          valueGetter: ({ row }) =>
            row.dealsInfo?.purchaseDeal?.price?.yearlyIncome &&
            `${row.dealsInfo?.purchaseDeal?.price?.yearlyIncome.toFixed(2)}%`,
        },
        {
          headerName: 'Доходность (₽)',
          field: 'totalIncome',
          selector: 'dealsInfo { purchaseDeal { price { totalIncome } } }',
          onSort: (sort) => [
            { dealsInfo: { purchaseDeal: { price: { totalIncome: sort } } } },
          ],
          valueGetter: ({ row }) =>
            row.dealsInfo?.purchaseDeal?.price?.totalIncome &&
            `${row.dealsInfo?.purchaseDeal?.price?.totalIncome.toLocaleString()}₽`,
        },
        {
          headerName: 'Статус',
          field: 'status',
          type: 'select',
          items: estateStatuses,
          width: 70,
        },
        // ...hiddenColumns,
      ] as HasuraDataTableColumnDef[];

      const boughtColumns = [
        { headerName: 'ID', field: 'id', width: 60 },
        {
          headerName: 'ЖК',
          field: 'residentialComplex',
          type: 'relationship',
          flex: 1,
          selector: 'residentialComplex { *name* }',
        },
        {
          headerName: 'Адрес',
          field: 'realAddress',
          flex: 1,
        },
        {
          headerName: 'Кол. комнат',
          width: 80,
          field: 'roomsNumber',
          type: 'select',
          items: RoomsNumbers,
        },
        {
          headerName: 'Дата сдачи',
          field: 'saleQueue',
          width: 90,
          type: 'relationship',
          selector: 'saleQueue { *releaseYear* releaseQuarter }',
          valueGetter({ value }) {
            return (
              value &&
              getSaleQueueName(value.releaseQuarter, value.releaseYear, true)
            );
          },
          onSort(sort) {
            return [{ saleQueue: { releaseDate: sort } }];
          },
        },
        {
          headerName: 'Отделка',
          field: 'finishing',
          width: 110,
          type: 'select',
          items: Finishing,
        },
        {
          headerName: 'Рекламная стоимость',
          field: 'price',
          width: 120,
          selector: 'price { adsPrice }',
          valueGetter({ value }) {
            return value?.adsPrice && `${value?.adsPrice.toLocaleString()}₽`;
          },
          onSort(sort) {
            return [{ price: { adsPrice: sort } }];
          },
        },
        {
          headerName: 'Тип',
          field: 'type',
          type: 'select',
          items: Types,
          width: 70,
        },
        {
          headerName: 'Дата покупки',
          width: 85,
          field: 'purchaseDate',
          selector: 'dealsInfo { purchaseDeal { dealDate } }',
          valueGetter: ({ row }) =>
            row.dealsInfo?.purchaseDeal?.dealDate &&
            dayjs(row.dealsInfo?.purchaseDeal?.dealDate).format('DD.MM.YYYY'),
        },
        {
          headerName: 'Доходность (%)',
          field: 'yearlyIncome',
          selector: 'price { yearlyIncome }',
          onSort: (sort) => [{ price: { yearlyIncome: sort } }],
          valueGetter: ({ row }) =>
            row.price?.yearlyIncome && `${row.price?.yearlyIncome.toFixed(2)}%`,
        },
        {
          headerName: 'Доходность (₽)',
          field: 'totalIncome',
          selector: 'price { totalIncome }',
          onSort: (sort) => [{ price: { totalIncome: sort } }],
          valueGetter: ({ row }) =>
            row.price?.totalIncome &&
            `${row.price?.totalIncome.toLocaleString()}₽`,
        },
        {
          field: 'tags',
          headerName: 'Теги',
          selector: 'tags { tagId }',
          sortable: false,
          width: 40,
          renderCell(params) {
            return (
              <TagsGroup ids={(params.value || []).map((x: any) => x.tagId)} />
            );
          },
        },
        {
          headerName: 'Статус',
          field: 'status',
          type: 'select',
          items: estateStatuses,
          width: 70,
        },
        // ...hiddenColumns,
      ] as HasuraDataTableColumnDef[];

      return [
        ...[...boughtColumns, ...hiddenColumns].map((x) => ({
          ...x,
          tabs: ['bought'],
        })),
        ...[...soldColumns, ...hiddenColumns].map((x) => ({
          ...x,
          tabs: ['sold'],
        })),
        ...[...boughtColumns, ...hiddenColumns].map((x) => ({
          ...x,
          tabs: ['all'],
        })),
      ] as HasuraDataTableColumnDef[];
    },
    [selectedTab],
  );

  const buildTab = (filter: Record<string, any>) => {
    return {
      sx: { height: '48px', minHeight: 'auto', pr: 1 },
      iconPosition: 'end' as const,
      icon: (
        <Box
          component="span"
          sx={{ color: 'transparent', width: 0, marginLeft: -1 }}
        >
          I
        </Box>
      ),
      badge: {
        subscribe: {
          source: 'estate',
          selection: 'aggregate {count}',
          extractCount: (data: any) => data.aggregate.count,
          aggregation: true,
          filter: { ...filter, isRemoved: { _eq: false } },
        },
        badgeProps: {
          sx: {
            '.MuiBadge-badge': { backgroundColor: 'rgba(255,255,255,0.75)' },
          },
        },
      },
      filter: { ...filter, isRemoved: { _eq: false } },
    };
  };

  return (
    <FormTab
      tab="estates"
      grid={false}
      sx={{ flexGrow: 1, display: 'flex', overflow: 'hidden' }}
    >
      {(isVisible) =>
        !isVisible ? null : (
          <Box
            sx={{
              overflow: 'hidden',
              flexGrow: 1,
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <EstatesTable
              id="client-estates-table"
              ref={tableRef}
              persistStateMode="query"
              queryPrefix="et"
              filterColumns={filterColumns}
              sx={{ mx: -2, borderColor: 'transparent' }}
              {...(addMode === 'link' && {
                components: {
                  FormDialog: LinkEstateToClientDialog,
                },
                automaticallyOpenEditPage: false,
              })}
              skeletonRowsCount={1}
              disableRemovedFilter
              tabsFilter={{
                tabs: [
                  {
                    id: 'bought',
                    label: 'Купленные',
                    ...buildTab({
                      clients: {
                        clientId: { _eq: clientId },
                        isOwner: { _eq: true },
                      },
                    }),
                  },
                  {
                    id: 'sold',
                    label: 'Проданные',
                    ...buildTab({
                      clients: {
                        clientId: { _eq: clientId },
                        isOwner: { _eq: false },
                      },
                    }),
                  },
                  {
                    id: 'all',
                    label: 'Все',
                    ...buildTab({
                      clients: {
                        clientId: { _eq: clientId },
                      },
                    }),
                  },
                ],
                tabsProps: {
                  onChange(e, value) {
                    setSelectedTab(value);
                  },
                },
                leftSlot: (
                  <Typography variant="subtitle1" sx={{ mr: 2 }}>
                    Объекты:{' '}
                  </Typography>
                ),
              }}
              formDialogProps={{
                ...({ clientId } as any),
                formProps: {
                  defaultValues: { clientId },
                },
                formSubmitterProps: {
                  onSubmit: () => {
                    setAddMode('new');
                  },
                },
              }}
              deleteProps={{
                async deleteAction(row) {
                  await hasura.request({
                    type: 'mutation',
                    action: 'update',
                    source: 'clientEstate',
                    where: {
                      estateId: { _eq: row.id },
                      clientId: { _eq: clientId },
                    },
                    set: hasura.removeUpdate,
                  });

                  return false;
                },
              }}
              deletable={false}
            />
          </Box>
        )
      }
    </FormTab>
  );
}
